import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import { useTranslation } from 'react-i18next';
import EditableNote, { hasText } from './EditableNote';

// The vehicle's service info (huoltotiedot): technical facts the mechanic needs on every job, such
// as the wheel bolt torque. Not secret, unlike the internal note: it also prints on the work order.

// Same cap as Vehicle.SERVICE_INFO_MAX_LENGTH in the API.
const SERVICE_INFO_MAX_LENGTH = 1000;

/** Info box with the vehicle's service info. Renders nothing when it is empty. */
export function VehicleServiceInfoBox({ serviceInfo, action, sx }) {
  const { t } = useTranslation();
  if (!hasText(serviceInfo)) return null;

  return (
    <Alert
      severity="info"
      icon={<BuildOutlinedIcon fontSize="inherit" />}
      data-testid="service-info-box"
      action={action}
      sx={{ borderRadius: '12px', '& .MuiAlert-message': { width: '100%' }, ...sx }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t('vehicleServiceInfo.title')}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} data-testid="service-info-text">
          {serviceInfo.trim()}
        </Typography>
      </Box>
    </Alert>
  );
}

/** The vehicle's service info, edited in place. `onSave(text)` persists it and throws on failure. */
export function VehicleServiceInfoEditor({ serviceInfo, canEdit, onSave, sx }) {
  const { t } = useTranslation();
  return (
    <EditableNote
      value={serviceInfo}
      canEdit={canEdit}
      onSave={onSave}
      sx={sx}
      testId={(part) => `service-info-${part}`}
      addIcon={<BuildOutlinedIcon />}
      maxLength={SERVICE_INFO_MAX_LENGTH}
      labels={{
        label: t('vehicleServiceInfo.title'),
        help: t('vehicleServiceInfo.help'),
        add: t('vehicleServiceInfo.add'),
        edit: t('vehicleServiceInfo.edit'),
        saveError: t('vehicleServiceInfo.saveError'),
      }}
      renderView={({ action, sx: viewSx }) => (
        <VehicleServiceInfoBox serviceInfo={serviceInfo} action={action} sx={viewSx} />
      )}
    />
  );
}
